import Box from "@/ui/atoms/Box";
import { Icon, Button, Flex } from "@proda-ai/ui-kit";
import React from "react";
import { creationTestIds } from "./utils";
import type { MultipropBasePropertyInfo } from "../dealTypes";

export type PropertyListProps<T extends MultipropBasePropertyInfo> = {
    properties: T[];
    addPropertyIsPending?: boolean;
    newProperty: () => void;
    editProperty: (propertyIndex: number) => void;
    onDeleteClicked?: (propertyIndex: number) => void;
    onRemoveFromListClicked?: (propertyIndex: number) => void;
    // Optional footer
    children?: (props: ChildrenProps) => React.ReactNode;
};

type ChildrenProps = {
    isSubmitDisabled: boolean;
};

const PropertyList = <T extends MultipropBasePropertyInfo>({
    properties,
    newProperty,
    editProperty,
    onDeleteClicked,
    onRemoveFromListClicked,
    children,
    addPropertyIsPending = false,
}: PropertyListProps<T>) => {
    const count = properties.length;

    const formatCountMessage = () => {
        // "There [is|are] currently **[1 property|n properties]** in this deal."
        const regularVariablePart = count === 1 ? "is" : `are`;
        const boldVariablePart =
            count === 1 ? "1 property" : `${count} properties`;
        return (
            <Box
                sx={{
                    alignContent: "left",
                    display: "flex",
                    flexDirection: "row",
                }}
            >
                There {regularVariablePart} currently&nbsp;
                <Box
                    sx={{ fontWeight: "bold" }}
                    data-testid={creationTestIds.propertyListCount}
                >
                    {boldVariablePart}
                </Box>
                &nbsp;in this project.
            </Box>
        );
    };

    return (
        <Box
            sx={{
                alignItems: "center",
                height: "100%",
                padding: "1rem",
                backgroundColor: "var(--proda-white-100)",
                width: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "left",
                    flexDirection: "column",
                    width: "700px",
                    margin: "auto",
                    flexGrow: 1,
                }}
            >
                <Box>
                    <Box
                        sx={{
                            fontSize: "1.2rem",
                            fontWeight: "bold",
                            marginBottom: "1rem",
                        }}
                        data-testid={creationTestIds.propertyListTitle}
                    >
                        Properties
                    </Box>
                    {formatCountMessage()}
                </Box>
                <Box>
                    {properties.map((property, index) => {
                        const isExistingProperty =
                            "propertyId" in property &&
                            property.propertyId !== null &&
                            property.propertyId !== undefined;
                        // Delete targets the Excelsior id; without one the click
                        // handler is a no-op, so show the icon disabled instead.
                        const hasExcelsiorId =
                            "excelsiorPropertyId" in property &&
                            property.excelsiorPropertyId !== null &&
                            property.excelsiorPropertyId !== undefined;
                        const removeFromListDisabled = count <= 1;
                        const deleteDisabled =
                            count <= 1 ||
                            !isExistingProperty ||
                            !hasExcelsiorId;
                        return (
                            <Box
                                sx={{
                                    maxWidth: "700px",
                                    height: "4rem",
                                    margin: "2rem auto",
                                    backgroundColor: "var(--proda-white-110)",
                                    padding: "1rem",
                                    fontSize: "0.8rem",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                                key={index}
                            >
                                <Flex
                                    justifyContent="space-between"
                                    alignItems="center"
                                    flexGap="medium"
                                >
                                    <Flex
                                        justifyContent="space-between"
                                        alignItems="left"
                                        flexGap="small"
                                    >
                                        <Box
                                            sx={{
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {property.propertyName},
                                        </Box>
                                        {property.fullAddress}
                                    </Flex>
                                    <Flex
                                        justifyContent="space-between"
                                        alignItems="center"
                                        flexDirection="row"
                                        flexGap="small"
                                    >
                                        <Button
                                            title="Edit property"
                                            icon="pen-line"
                                            isGhost
                                            iconColor="proda-navy-blue"
                                            pattern="primary"
                                            onClick={() => {
                                                void editProperty(index);
                                            }}
                                            data-testid={creationTestIds.propertyListEditProperty(
                                                index,
                                            )}
                                        />
                                        {onRemoveFromListClicked && (
                                            <Button
                                                title="Remove property from project"
                                                icon="xmark"
                                                isGhost
                                                iconColor={
                                                    removeFromListDisabled
                                                        ? "text-color-disabled"
                                                        : "proda-navy-blue"
                                                }
                                                onClick={() => {
                                                    onRemoveFromListClicked(
                                                        index,
                                                    );
                                                }}
                                                data-testid={creationTestIds.propertyListRemovePropertyFromList(
                                                    index,
                                                )}
                                                isDisabled={
                                                    removeFromListDisabled
                                                }
                                            />
                                        )}
                                        {onDeleteClicked && (
                                            <Button
                                                title="Delete property"
                                                icon="trash"
                                                isGhost
                                                iconColor={
                                                    deleteDisabled
                                                        ? "text-color-disabled"
                                                        : "proda-navy-blue"
                                                }
                                                isDisabled={deleteDisabled}
                                                onClick={() => {
                                                    onDeleteClicked(index);
                                                }}
                                                data-testid={creationTestIds.propertyListDeleteProperty(
                                                    index,
                                                )}
                                            />
                                        )}
                                    </Flex>
                                </Flex>
                            </Box>
                        );
                    })}
                </Box>
                <Box
                    component="button"
                    type="button"
                    onClick={newProperty}
                    sx={{
                        padding: "0.5rem 2rem",
                        width: "200px",
                        border: "none",
                        borderRadius: "4px",
                        backgroundColor: !addPropertyIsPending
                            ? "var(--primary-color)"
                            : "var(--disabled-bg)",
                        color: "var(--proda-white)",
                        cursor: "pointer",
                        alignContent: "center",
                        justifyContent: "space-between",
                        marginTop: "1rem",
                        marginBottom: "1rem",
                    }}
                    disabled={addPropertyIsPending}
                    data-testid={
                        creationTestIds.propertyListCreatePropertyButton
                    }
                >
                    <Icon icon="plus" />
                    &nbsp;New Property
                </Box>
            </Box>
            {children !== undefined && (
                <Box
                    sx={{
                        padding: "2rem",
                        borderTop: "1px solid var(--proda-white-150)",
                        marginTop: "auto",
                        width: "100%",
                    }}
                >
                    {children({
                        isSubmitDisabled: count == 0,
                    })}
                </Box>
            )}
        </Box>
    );
};

export default PropertyList;
